import { addRecommendation } from './hv-drawer-cart.js';

const recommendationCache = new Map();

class HvCartRecommendations extends HTMLElement {
  #observer;
  #abortController;
  #activated = false;
  #activeProduct = null;
  #selectedOptions = [];

  #quickTrigger = null;
  #drawerHost;
  #refreshWhenClosed = () => this.#load();

  static get observedAttributes() {
    return ['data-product-id', 'data-exclude-handles', 'data-currency'];
  }

  connectedCallback() {
    if (this.#observer || this.#activated) return;

    this.#activated = true;
    this.#drawerHost = this.closest('theme-drawer');
    this.#drawerHost?.addEventListener('theme-drawer:close', this.#refreshWhenClosed);
    const initial = this.querySelector('[data-hv-recs-initial]');
    if (initial) {
      try {
        const excluded = new Set((this.dataset.excludeHandles || '').split(','));
        const products = JSON.parse(initial.textContent).filter(product => product.available && !excluded.has(product.handle)).slice(0, 6);
        const rail = this.querySelector('[data-hv-recs-rail]');
        rail.replaceChildren(...products.map(product => this.#createCard(product)));
        if (products.length) this.querySelector('[data-hv-recs-shell]').dataset.ready = 'true';
      } catch {}
    }
    this.#load();
  }

  disconnectedCallback() {
    this.#drawerHost?.removeEventListener('theme-drawer:close', this.#refreshWhenClosed);
    this.#drawerHost = null;
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#abortController?.abort();
    this.#abortController = undefined;
    this.#activated = false;
    this.#activeProduct = null;
    this.#selectedOptions = [];
    this.#quickTrigger = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.isConnected || !this.#activated) return;
    if (!['data-product-id', 'data-exclude-handles', 'data-currency'].includes(name)) return;
    this.#load();
  }

  async #load() {
    this.#clearGlobalError();
    const productId = this.dataset.productId;
    const shell = this.querySelector('[data-hv-recs-shell]');
    const rail = this.querySelector('[data-hv-recs-rail]');
    if (!(shell instanceof HTMLElement) || !(rail instanceof HTMLElement)) return;

    if (!productId) {
      this.hidden = true;
      shell.removeAttribute('data-ready');
      return;
    }

    this.hidden = false;
    if (!rail.children.length) shell.removeAttribute('data-ready');

    this.#abortController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=10&intent=related`;

    try {
      let products = recommendationCache.get(url);
      if (!products) {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Recommendations request failed (${response.status})`);
        const data = await response.json();
        products = Array.isArray(data?.products) ? data.products : [];
        recommendationCache.set(url, products);
      }

      if (controller.signal.aborted) return;

      const excluded = new Set(
        (this.dataset.excludeHandles || '')
          .split(',')
          .map((handle) => handle.trim())
          .filter(Boolean)
      );

      const visibleProducts = products
        .filter((product) => product?.available && product?.handle && !excluded.has(product.handle))
        .slice(0, 6);

      if (!visibleProducts.length) {
        this.hidden = !rail.children.length;
        return;
      }

      if (this.#drawerHost?.hasAttribute('open') && rail.children.length) return;
      rail.replaceChildren(...visibleProducts.map((product) => this.#createCard(product)));
      shell.dataset.ready = 'true';
      this.hidden = false;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[H&V cart recommendations]', error);
        this.hidden = !rail.children.length;
      }
    } finally {
      if (this.#abortController === controller) this.#abortController = undefined;
    }
  }

  #createCard(product) {
    const article = document.createElement('article');
    article.className = 'hv-cart-recs__card';

    const imageUrl = this.#getImageUrl(product);

    const mediaButton = document.createElement('button');
    mediaButton.type = 'button';
    mediaButton.className = 'hv-cart-recs__image-button';
    mediaButton.setAttribute('aria-label', `Add ${product.title || 'recommended item'} to cart`);
    mediaButton.setAttribute('aria-expanded', 'false');
    mediaButton.addEventListener('click', () => this.#handleProductAction(product, mediaButton));

    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'hv-cart-recs__image';
      image.src = this.#withImageWidth(imageUrl, 320);
      image.alt = product.title || '';
      image.loading = 'eager';
      image.decoding = 'async';
      image.width = 240;
      image.height = 320;
      mediaButton.appendChild(image);
    }

    const icon = document.createElement('span');
    icon.className = 'hv-cart-recs__add-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false">
        <path d="M7.5 8.25V7a4.5 4.5 0 0 1 9 0v1.25"/>
        <path d="M5.75 8.25h12.5l.85 11.25H4.9L5.75 8.25Z"/>
        <path d="M12 11.4v4.2M9.9 13.5h4.2"/>
      </svg>`;
    mediaButton.appendChild(icon);

    const body = document.createElement('div');
    body.className = 'hv-cart-recs__body';

    const title = document.createElement('button');
    title.type = 'button';
    title.className = 'hv-cart-recs__title';
    title.textContent = product.title || 'Recommended item';
    title.addEventListener('click', () => this.#handleProductAction(product, mediaButton));

    const price = document.createElement('div');
    price.className = 'hv-cart-recs__price';
    this.#renderPrice(price, product);

    body.append(title, price);
    article.append(mediaButton, body);
    return article;
  }

  #handleProductAction(product, trigger) {
    this.#clearGlobalError();

    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const availableVariants = variants.filter((variant) => variant?.available);
    const options = this.#normalOptions(product);
    const hasRealOptions = options.some(
      (option) => option.values.length > 1 || !/^default title$/i.test(option.values[0] || '')
    );

    if (variants.length === 1 && availableVariants.length === 1 && !hasRealOptions) {
      this.#addVariant(availableVariants[0].id, product, trigger);
      return;
    }

    if (variants.length === 1 && availableVariants.length === 1) {
      this.#addVariant(availableVariants[0].id, product, trigger);
      return;
    }

    this.#openQuickPicker(product, trigger);
  }

  #normalOptions(product) {
    const rawOptions = Array.isArray(product?.options) ? product.options : [];
    return rawOptions
      .map((option, index) => {
        if (typeof option === 'string') {
          const values = Array.from(
            new Set(
              (product.variants || [])
                .map((variant) => variant?.options?.[index] ?? variant?.[`option${index + 1}`])
                .filter(Boolean)
            )
          );
          return { name: option, position: index + 1, values };
        }
        return {
          name: option?.name || `Option ${index + 1}`,
          position: Number(option?.position) || index + 1,
          values: Array.isArray(option?.values) ? option.values.filter(Boolean) : [],
        };
      })
      .filter((option) => option.values.length > 0);
  }

  #openQuickPicker(product, trigger) {
    const panel = this.querySelector('[data-hv-recs-quick]');
    if (!(panel instanceof HTMLElement)) return;

    // Reset any previously open picker first so only one recommendation can
    // expose an expanded state at a time. Do not restore focus while switching.
    this.#closeQuickPicker(false);
    this.#quickTrigger = trigger instanceof HTMLElement ? trigger : null;
    if (this.#quickTrigger) this.#quickTrigger.setAttribute('aria-expanded', 'true');

    this.#activeProduct = product;
    const options = this.#normalOptions(product);
    this.#selectedOptions = options.map((option) => (option.values.length === 1 ? option.values[0] : null));

    panel.replaceChildren();
    panel.hidden = false;
    panel.dataset.open = 'true';

    const header = document.createElement('div');
    header.className = 'hv-cart-recs__quick-head';

    const titleWrap = document.createElement('div');
    const eyebrow = document.createElement('span');
    eyebrow.className = 'hv-cart-recs__quick-eyebrow';
    eyebrow.textContent = 'Quick add';
    const title = document.createElement('a');
    const productUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
    title.href = productUrl;
    title.dataset.hvQuickDetails = '';
    title.className = 'hv-cart-recs__quick-title';
    title.textContent = product.title || 'Select options';
    const details = document.createElement('a');
    details.className = 'hv-cart-recs__quick-details';
    details.dataset.hvQuickDetails = '';
    details.href = productUrl;
    details.textContent = this.dataset.viewDetails || 'View details';
    titleWrap.append(eyebrow, title, details);
    const media = document.createElement('a');
    media.className = 'hv-cart-recs__quick-media';
    media.href = productUrl;
    media.dataset.hvQuickDetails = '';
    media.setAttribute('aria-label', product.title || details.textContent);
    const imageUrl = this.#getImageUrl(product);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = this.#withImageWidth(imageUrl, 160);
      image.alt = product.title || '';
      image.width = 48;
      image.height = 64;
      image.dataset.hvQuickImage = '';
      media.appendChild(image);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'hv-cart-recs__quick-close';
    close.setAttribute('aria-label', 'Close quick add');
    close.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m5 5 10 10M15 5 5 15"/></svg>';
    close.addEventListener('click', () => this.#closeQuickPicker(true));
    header.append(media, titleWrap, close);

    const groups = document.createElement('div');
    groups.className = 'hv-cart-recs__quick-groups';

    options.forEach((option, optionIndex) => {
      if (options.length === 1 && option.values.length === 1 && /^default title$/i.test(option.values[0])) return;

      const group = document.createElement('fieldset');
      group.className = 'hv-cart-recs__quick-group';
      group.dataset.optionIndex = String(optionIndex);

      const legend = document.createElement('legend');
      legend.textContent = option.name;
      group.appendChild(legend);

      const choices = document.createElement('div');
      choices.className = 'hv-cart-recs__quick-choices';

      option.values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hv-cart-recs__option';
        button.textContent = value;
        button.dataset.optionIndex = String(optionIndex);
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', String(this.#selectedOptions[optionIndex] === value));
        button.addEventListener('click', () => {
          this.#selectedOptions[optionIndex] = value;
          this.#clearInvalidLaterSelections(optionIndex);
          this.#syncOptionState(panel);
        });
        choices.appendChild(button);
      });

      group.appendChild(choices);
      groups.appendChild(group);
    });

    const status = document.createElement('p');
    status.className = 'hv-cart-recs__quick-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.dataset.hvQuickStatus = '';

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'hv-cart-recs__quick-add';
    add.dataset.hvQuickAdd = '';
    add.disabled = true;
    add.textContent = 'Select options';
    add.addEventListener('click', () => {
      const variant = this.#getSelectedVariant();
      if (variant?.available) this.#addVariant(variant.id, product, add);
    });

    panel.append(header, groups, status, add);
    this.#syncOptionState(panel);

    requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      panel.scrollIntoView({ block: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
      const firstUnselected = panel.querySelector('.hv-cart-recs__option[aria-pressed="false"]:not(:disabled)');
      if (firstUnselected instanceof HTMLButtonElement) firstUnselected.focus({ preventScroll: true });
      else add.focus({ preventScroll: true });
    });

  }

  #closeQuickPicker(restoreFocus = false) {
    const trigger = this.#quickTrigger;
    const panel = this.querySelector('[data-hv-recs-quick]');
    if (panel instanceof HTMLElement) {
      panel.hidden = true;
      panel.removeAttribute('data-open');
      panel.replaceChildren();
    }
    this.querySelectorAll('.hv-cart-recs__image-button[aria-expanded="true"]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
    this.#activeProduct = null;
    this.#selectedOptions = [];
    this.#quickTrigger = null;

    if (restoreFocus && trigger instanceof HTMLElement && trigger.isConnected) {
      requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
    }
  }

  #syncOptionState(panel) {
    const product = this.#activeProduct;
    if (!product) return;

    const status = panel.querySelector('[data-hv-quick-status]');
    if (status instanceof HTMLElement) status.classList.remove('is-error');

    const optionButtons = panel.querySelectorAll('.hv-cart-recs__option');
    optionButtons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const optionIndex = Number(button.dataset.optionIndex);
      const value = button.dataset.optionValue || '';
      const canLeadToAvailableVariant = (product.variants || []).some((variant) => {
        if (!variant?.available) return false;
        const variantOptions = this.#variantOptions(variant);
        if (variantOptions[optionIndex] !== value) return false;

        return this.#selectedOptions.every((selectedValue, index) => {
          if (index >= optionIndex || !selectedValue) return true;
          return variantOptions[index] === selectedValue;
        });
      });

      button.disabled = !canLeadToAvailableVariant;
      button.setAttribute('aria-pressed', String(this.#selectedOptions[optionIndex] === value));
    });

    const add = panel.querySelector('[data-hv-quick-add]');
    const selectedVariant = this.#getSelectedVariant();
    this.#syncQuickImage(panel, product, selectedVariant);
    const allSelected = this.#selectedOptions.length > 0 && this.#selectedOptions.every(Boolean);

    if (add instanceof HTMLButtonElement) {
      add.disabled = !allSelected || !selectedVariant?.available;
      add.textContent = !allSelected ? 'Select options' : selectedVariant?.available ? 'Add to cart' : 'Unavailable';
    }

    if (status instanceof HTMLElement) {
      if (!allSelected) {
        const options = this.#normalOptions(product);
        const missing = options
          .filter((_, index) => !this.#selectedOptions[index])
          .map((option) => option.name.toLowerCase());
        status.textContent = missing.length ? `Select ${missing.join(' & ')}` : '';
      } else if (selectedVariant?.available) {
        status.textContent = this.#formatMoney(selectedVariant.price ?? product.price_min ?? product.price ?? 0);
      } else {
        status.textContent = 'This combination is unavailable';
      }
    }
  }

  #syncQuickImage(panel, product, selectedVariant) {
    const baseUrl = `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product.handle)}`;
    panel.querySelectorAll('[data-hv-quick-details]').forEach(link => {
      link.href = selectedVariant ? `${baseUrl}?variant=${selectedVariant.id}` : baseUrl;
    });
    const image = panel.querySelector('[data-hv-quick-image]');
    if (!image) return;
    const options = this.#normalOptions(product);
    const colourIndex = options.findIndex(option => /colou?r/i.test(option.name));
    const colour = this.#selectedOptions[colourIndex];
    const colourVariant = colour && (product.variants || []).find(variant =>
      this.#variantOptions(variant)[colourIndex] === colour && variant.featured_image);
    const source = this.#getImageUrl(selectedVariant) || this.#getImageUrl(colourVariant);
    const url = source ? this.#withImageWidth(source, 160) : image.getAttribute('src');
    image.dataset.requestedSrc = url;
    if (image.getAttribute('src') === url) return;
    const preload = new Image();
    preload.onload = () => {
      if (image.isConnected && this.#activeProduct === product && image.dataset.requestedSrc === url) {
        image.src = url;
      }
    };
    preload.src = url;
  }

  #clearInvalidLaterSelections(changedIndex) {
    const product = this.#activeProduct;
    if (!product) return;

    for (let index = changedIndex + 1; index < this.#selectedOptions.length; index += 1) {
      const selectedValue = this.#selectedOptions[index];
      if (!selectedValue) continue;

      const stillValid = (product.variants || []).some((variant) => {
        if (!variant?.available) return false;
        const values = this.#variantOptions(variant);
        return this.#selectedOptions.every((selected, selectedIndex) => {
          if (selectedIndex > index || !selected) return true;
          return values[selectedIndex] === selected;
        });
      });

      if (!stillValid) this.#selectedOptions[index] = null;
    }
  }

  #variantOptions(variant) {
    if (Array.isArray(variant?.options)) return variant.options;
    return [variant?.option1, variant?.option2, variant?.option3].filter((value) => value != null);
  }

  #getSelectedVariant() {
    const product = this.#activeProduct;
    if (!product || !this.#selectedOptions.length || !this.#selectedOptions.every(Boolean)) return null;
    return (product.variants || []).find((variant) => {
      const values = this.#variantOptions(variant);
      return this.#selectedOptions.every((selected, index) => values[index] === selected);
    });
  }

  async #addVariant(variantId, product, trigger) {
    if (!variantId) return;
    this.#closeQuickPicker(true);
    try {
      await addRecommendation(Number(variantId), product, this.dataset.currency);
      this.#announce('Added to cart.');
    } catch (error) {
      this.#showQuickError(error.message || 'Unable to add this item.');
    }
  }

  #showQuickError(message) {
    this.#announce(message);
    const panel = this.querySelector('[data-hv-recs-quick]');
    const status = panel?.querySelector('[data-hv-quick-status]');
    if (status instanceof HTMLElement) {
      status.textContent = message;
      status.classList.add('is-error');
      return;
    }

    // A single-variant recommendation adds directly without opening the picker.
    // Give that failure path visible feedback instead of failing silently.
    const globalError = this.querySelector('[data-hv-recs-error]');
    if (globalError instanceof HTMLElement) {
      globalError.textContent = message;
      globalError.hidden = false;
    }
  }

  #clearGlobalError() {
    const globalError = this.querySelector('[data-hv-recs-error]');
    if (!(globalError instanceof HTMLElement)) return;
    globalError.textContent = '';
    globalError.hidden = true;
  }

  #announce(message) {
    const live = this.querySelector('[data-hv-recs-status]');
    if (!(live instanceof HTMLElement)) return;
    // Clearing first ensures repeated identical messages are announced again.
    live.textContent = '';
    requestAnimationFrame(() => {
      if (live.isConnected) live.textContent = message || '';
    });
  }

  #renderPrice(container, product) {
    const price = Number(product.price_min ?? product.price ?? 0);
    const compareAt = Number(product.compare_at_price_min || product.compare_at_price || 0);
    const prefix = product.price_varies ? 'From ' : '';

    if (compareAt > price && price > 0) {
      const compare = document.createElement('span');
      compare.className = 'hv-cart-recs__compare';
      compare.textContent = this.#formatMoney(compareAt);

      const sale = document.createElement('span');
      sale.className = 'hv-cart-recs__sale';
      sale.textContent = `${prefix}${this.#formatMoney(price)}`;
      container.append(compare, sale);
      return;
    }

    const regular = document.createElement('span');
    regular.className = 'hv-cart-recs__regular';
    regular.textContent = `${prefix}${this.#formatMoney(price)}`;
    container.appendChild(regular);
  }

  #formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || this.dataset.currency || 'GBP';
    const locale = document.documentElement.lang || 'en-GB';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(Number(cents || 0) / 100);
    } catch {
      return `£${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  #getImageUrl(product) {
    const featured = product?.featured_image;
    if (typeof featured === 'string') return featured;
    if (featured?.url) return featured.url;
    if (featured?.src) return featured.src;

    const firstImage = Array.isArray(product?.images) ? product.images[0] : null;
    if (typeof firstImage === 'string') return firstImage;
    return firstImage?.url || firstImage?.src || '';
  }

  #withImageWidth(url, width) {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.hostname.includes('cdn.shopify.com')) parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch {
      return url;
    }
  }
}

if (!customElements.get('hv-cart-recommendations')) {
  customElements.define('hv-cart-recommendations', HvCartRecommendations);
}
